import { ChangeDetectorRef, Component, OnInit } from '@angular/core'
import { UntypedFormBuilder } from '@angular/forms'
import { ActivatedRoute } from '@angular/router'
import { NgbDate } from '@ng-bootstrap/ng-bootstrap'
import { ActiveCartFacade, DeliveryMode } from '@spartacus/cart/base/root'
import {
  CheckoutConfigService,
  CheckoutDeliveryModeComponent,
  CheckoutStepService
} from '@spartacus/checkout/base/components'
import { CheckoutDeliveryModesService } from '@spartacus/checkout/base/core'
import { CheckoutSupportedDeliveryModesQueryResetEvent } from '@spartacus/checkout/base/root'
import { EventService, GlobalMessageService } from "@spartacus/core"
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs'
import { filter, map, switchMap, take, tap } from 'rxjs/operators'
import { EbCart } from '../../../cart/interface/eb-cart.interface'
import {
  EbAvailability,
  EbAvailabilityItem
} from '../../../product/interfaces/eb-availability.interface'
import { EbAvailabilityService } from '../../../product/services/facades/eb-availability.service'
import { EbCheckoutCommandService } from '../../commands/eb-checkout-command.service'
import { EbCheckoutDeliveryService } from '../../services/facades/eb-checkout-delivery.service'

@Component({
    selector: 'eb-delivery-mode',
    templateUrl: './eb-delivery-mode.component.html',
    styleUrls: ['./eb-delivery-mode.component.scss'],
    standalone: false
})
export class EbDeliveryModeComponent extends CheckoutDeliveryModeComponent implements OnInit{
  activeCart$: Observable<EbCart> = this.activeCartService.getActive()
  deliveryMinDate$ = new BehaviorSubject<Date>(undefined)
  deliveryStartDate: Date
  isFetchingNewStartDate$ = new BehaviorSubject(false)
  initialLoadOfShippingDate$ = new BehaviorSubject(false)
  dateInput$ = new Subject<any>()
  isSaved = false
  shippingDateChangedByCustomer = false
  fetchedDeliveryModes$ = new BehaviorSubject(true)
  supportedDeliveryModes: DeliveryMode[]

  activeDeliveryMode = '01'
  activeShippingMethod = 'complete'
  subscription = new Subscription()
  dateChangeSubscription = new Subscription()
  cartContainsUnknownATP = false
  isRequestedShippingDaySet$ =   new BehaviorSubject(false)
  constructor(
    public formBuilder: UntypedFormBuilder,
    protected ebCheckoutDeliveryService: EbCheckoutDeliveryService,
    checkoutConfigService: CheckoutConfigService,
    activatedRoute: ActivatedRoute,
    protected ebActivatedRoute: ActivatedRoute,
    checkoutStepService: CheckoutStepService,
    protected activeCartService: ActiveCartFacade,
    protected availabilityService: EbAvailabilityService,
    protected deliveryModesService: CheckoutDeliveryModesService,
    protected globalMessageService: GlobalMessageService,
    private readonly cd: ChangeDetectorRef,
    protected eventService: EventService,
    protected ebcheckoutCommandService : EbCheckoutCommandService
  ) {
    super(
      formBuilder,
      checkoutConfigService,
      activatedRoute,
      checkoutStepService,
      deliveryModesService,
      activeCartService
    )
  }

  ngOnInit() {
    this.getDeliveryModes()
    this.syncDeliveryModeWithCart()
    this.syncShippingMethodWithCart()
    this.initializeEarliestShippingDate()
    this.initializeDateInput()
    this.isRequestedShippingDaySet$.next(false)
    this.ebcheckoutCommandService.isRequestedShippingDaySet$.subscribe(items => this.isRequestedShippingDaySet$.next(items));
  }

  get deliveryMinDate() {
    return this.deliveryMinDate$.value
  }

  handleShippingMethodChange(selectedMethod: string) {
    this.ebCheckoutDeliveryService.setCompleteDelivery(
      selectedMethod === 'complete' || selectedMethod === 'shipping-date'
    )
    if (selectedMethod === 'complete') {
      this.deliveryStartDate = this.deliveryMinDate
      this.setRequestedShippingDateOnCart()
    }
    if (selectedMethod === 'partial') {
      this.removeRequestedShippingDateOnCart()
    }
  }

  initializeEarliestShippingDate() {
    let cartShippingDate = null
    this.initialLoadOfShippingDate$.next(true)
    this.subscription.add(
      this.activeCart$
        .pipe(
          take(1),
          map((cart: EbCart) => {
            this.isFetchingNewStartDate$.next(true)
            if (cart.requestedShippingDate && cart.completeDelivery) {
              cartShippingDate = new Date(cart.requestedShippingDate)
              this.shippingDateChangedByCustomer = cart.customerSpecificRequestedShippingDate
            }
            return (
              cart?.entries?.map(entry => {
                return {
                  product: entry.product.code,
                  quantity: entry.quantity
                } as EbAvailabilityItem
              }) || []
            )
          }),
          switchMap(entries => {
            if (entries.length > 0) {
              return this.availabilityService.fetchAvailabilities(entries, false).pipe(
                tap(availability => {
                  this.subscription.add(
                    this.availabilityService.setEarliestShippingDate(availability.items).subscribe()
                  )
                }),
                map(availability =>
                  this.getLatestDateInAvailability(availability, cartShippingDate)
                )
              )
            }
            return undefined
          })
        )
        .subscribe(latestDate => {
          if (!this.isSaved) {
            this.ebCheckoutDeliveryService.setRequestedShippingDate(
              (this.deliveryStartDate?.toJSON() ?? latestDate)?.split('T')[0],
              false,
              this.shippingDateChangedByCustomer,
              false
            )
            this.isSaved = true
          }
          this.deliveryMinDate$.next(latestDate)
          if (this.deliveryStartDate === null) {
            this.deliveryStartDate = cartShippingDate
          }
          this.initialLoadOfShippingDate$.next(false)
          this.cd.detectChanges()
        })
    )
  }

  getLatestDateInAvailability(availability: EbAvailability, cartShippingDate: Date) {
    let latestDate = null
    availability?.items?.forEach(item => {
      if (item.availableDate) {
        const itemDate = new Date(item.availableDate)
        if (!latestDate || itemDate.getTime() > latestDate.getTime()) {
          latestDate = itemDate
        }
      }
    })

    if (
      cartShippingDate &&
      latestDate &&
      this.shippingDateChangedByCustomer &&
      cartShippingDate.getTime() > latestDate.getTime()
    ) {
      this.deliveryStartDate = cartShippingDate
    } else {
      this.deliveryStartDate = latestDate
      this.shippingDateChangedByCustomer = false
    }
    this.isFetchingNewStartDate$.next(false)
    return latestDate || this.returnThreeDaysInAdvanceDate()
  }

  syncDeliveryModeWithCart() {
    this.subscription.add(
      this.activeCartService
        .getActive()
        .pipe(
          take(1),
          map(activeCart => {
            if (activeCart.deliveryMode?.code) {
              this.activeDeliveryMode = activeCart.deliveryMode?.code
            } else {
              this.activeDeliveryMode = '01'
              this.subscription.add(this.deliveryModesService.setDeliveryMode('01').subscribe())
            }
          })
        )
        .subscribe()
    )
  }

  syncShippingMethodWithCart() {
    this.subscription.add(
      this.activeCart$
        .pipe(
          take(1),
          map((activeCart: EbCart) => {
            if (activeCart.completeDelivery) {
              this.activeShippingMethod = 'complete'
              if (
                this.deliveryMinDate &&
                this.deliveryMinDate !== activeCart.requestedShippingDate
              ) {
                this.deliveryStartDate = this.deliveryMinDate
                this.activeShippingMethod = 'shipping-date'
              }
            } else {
              this.activeShippingMethod = 'partial'
              this.removeRequestedShippingDateOnCart()
            }
          })
        )
        .subscribe()
    )
  }

  disableWeekendDays(date: NgbDate) {
    const currentDay = new Date(Date.UTC(date.year, date.month - 1, date.day))
    return currentDay.getDay() === 0 || currentDay.getDay() === 6
  }

  dateChange(newDate: any) {
    if (newDate) {
      this.dateInput$.next(newDate)
    }
  }

  setRequestedShippingDateOnCart() {
    this.ebCheckoutDeliveryService.setRequestedShippingDate(
      this.deliveryStartDate.toJSON(),
      false,
      this.shippingDateChangedByCustomer
    )
  }

  removeRequestedShippingDateOnCart() {
    this.ebCheckoutDeliveryService.setRequestedShippingDate(null, false, false)
  }

  next(): void {
    this.checkoutStepService.next(this.ebActivatedRoute)
  }

  ngOnDestroy() {
    this.subscription.unsubscribe()
    this.dateChangeSubscription?.unsubscribe()
    this.dateInput$.complete()
  }

  returnThreeDaysInAdvanceDate() {
    const threeDaysInAdvance = new Date()
    threeDaysInAdvance.setDate(threeDaysInAdvance.getDate() + 3)
    return threeDaysInAdvance.toISOString()
  }

  getDeliveryModes() {
    this.eventService.dispatch(
      {},
      CheckoutSupportedDeliveryModesQueryResetEvent
    );

    this.subscription.add(
      this.deliveryModesService.getSupportedDeliveryModes()
      .pipe(
        filter((deliveryModes) => deliveryModes != null),
      )
        .subscribe((deliveryModes) => {
          this.supportedDeliveryModes = deliveryModes
          this.fetchedDeliveryModes$.next(true)
        })
    )
  }

  private initializeDateInput() {
    this.dateInput$.subscribe(newDate => {
      this.isFetchingNewStartDate$.next(true)
     this.dateChangeSubscription.add(
        this.activeCart$
          .subscribe(activeCart => {
            const currentDate = new Date(
              Date.UTC(newDate.year, newDate.month - 1, newDate.day)
            ).toISOString()
            if (
              currentDate.split('T')[0] ===
              activeCart.requestedShippingDate?.toLocaleString().split('T')[0]
            ) {
              this.deliveryStartDate = new Date(
                Date.UTC(newDate.year, newDate.month - 1, newDate.day)
              )
            }
          }),
      )
      const date = new Date(Date.UTC(newDate.year, newDate.month - 1, newDate.day))
      this.ebCheckoutDeliveryService.setRequestedShippingDate(
        date.toJSON()?.split('T')[0],
        true,
        true,
      ).add(() => {
        this.isFetchingNewStartDate$.next(false)
      })
    })
  }
}
